import { useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import {
    SubmitErrorHandler,
    SubmitHandler,
    useFormContext,
} from "react-hook-form";

import { AsyncStatuses } from "@src/types/shared";

import { submitContact } from "@api";

import { alertActions } from "@features/alert/alertSlice";

import { useAppDispatch } from "@hooks/reduxHooks";
import useRecaptcha from "@hooks/useRecaptcha";

import { IContactFormData } from "@Index/components/Contact/types/contact";

const useFormHandlers = (recaptcha: React.RefObject<ReCAPTCHA>) => {
    const dispatch = useAppDispatch();
    const [submitting, setSubmitting] = useState<AsyncStatuses>("init");
    const { reset, setValue } = useFormContext<IContactFormData>();
    const { verifyCaptcha } = useRecaptcha<IContactFormData>(recaptcha);

    const onSubmit: SubmitHandler<IContactFormData> = async data => {
        setSubmitting("pending");
        if (verifyCaptcha()) {
            await submitContact(data)
                .then(_ => {
                    dispatch(
                        alertActions.success({
                            text: "Thank you for reaching out to us. We will respond as soon as possible.",
                            dismissible: true,
                        }),
                    );
                    setSubmitting("success");
                })
                .catch(e => {
                    dispatch(
                        alertActions.error({
                            title: `The server encountered a problem sending your contact request.`,
                            text: "If the problem continues, please try emailing us directly.",
                            dismissible: true,
                        }),
                    );
                    setSubmitting("failed");
                });
        }
        reset();
        setValue("recaptcha", null);
        setValue("ageLevel", null);
        setSubmitting("init");
    };

    const onError: SubmitErrorHandler<IContactFormData> = (): void => {
        verifyCaptcha();
    };

    return { onSubmit, onError, submitting };
};

export default useFormHandlers;
